// Health Knowledge Base — structured chunks extracted from cancerRecommendations.js
// and healthScreeningRecommendations.js for RAG retrieval.

const healthKnowledgeBase = [
  // ─── LUNG CANCER ───
  {
    id: "lung_screening",
    topic: "lung_cancer_screening",
    content:
      "Lung cancer screening is recommended via low-dose CT (LDCT) for adults aged 50–80 who have a 20+ pack-year smoking history and currently smoke or quit within the past 15 years. USPSTF 2021 Grade B recommendation. Baseline lifetime risk: ~6.3% for males, ~5.8% for females (NCI SEER).",
    citations: ["USPSTF 2021 Grade B", "NCI SEER Cancer Statistics 2017-2021"],
    keywords: ["lung", "cancer", "screening", "ldct", "ct scan", "smoking", "pack-year", "low-dose"],
  },
  {
    id: "lung_smoking_risk",
    topic: "lung_cancer_risk_factors",
    content:
      "Smoking is the dominant risk factor for lung cancer. Risk multipliers by pack-years: light (<10 pk-yr) 2.0x, moderate (10-20) 5.0x, heavy (20-30) 18.0x, very heavy (30+) 25.0x baseline risk. Zhang et al. 2022 (PMID:35194190) found heavy smoking HR 4.27, combined with high genetic risk HR 6.19, in 345,794 UK Biobank participants. Doll & Peto 2004 (PMID:15213107) 50-year British doctors cohort: heavy long-term smokers ~15-25x lung cancer mortality vs never-smokers.",
    citations: [
      "Zhang et al. 2022, Br J Cancer, PMID:35194190",
      "Doll & Peto 2004, BMJ, PMID:15213107",
    ],
    keywords: ["lung", "cancer", "smoking", "cigarette", "pack-year", "risk", "tobacco"],
  },
  {
    id: "lung_secondhand_smoke",
    topic: "lung_cancer_risk_factors",
    content:
      "Secondhand smoke exposure increases lung cancer risk by ~24-38%. Possenti et al. 2024 (PMID:39537242) meta-analysis of 97 studies: RR 1.24 (1.16-1.32) overall, 1.38 for workplace exposure. Wang et al. 2024 (PMID:38253312) identified secondhand smoke as one of 7 key risk factors for personalized lung cancer screening in a study of 1,031,911 Chinese participants.",
    citations: [
      "Possenti et al. 2024, Eur Respir Rev, PMID:39537242",
      "Wang et al. 2024, Chest, PMID:38253312",
    ],
    keywords: ["secondhand", "smoke", "passive", "lung", "cancer", "exposure", "risk"],
  },
  {
    id: "lung_ethnicity",
    topic: "lung_cancer_risk_factors",
    content:
      "Black women have higher lung cancer incidence despite lower smoking prevalence, supporting ethnicity-specific risk adjustments. Bethea et al. 2022 (PMID:35325667). Asian populations have ~30% lower lung cancer incidence compared to Caucasians (risk multiplier 0.7x). African Americans have ~20% higher incidence (1.2x).",
    citations: ["Bethea et al. 2022, Cancer Epidemiol, PMID:35325667", "NCI SEER"],
    keywords: ["lung", "cancer", "ethnicity", "race", "african", "black", "asian", "disparity"],
  },

  // ─── BREAST CANCER ───
  {
    id: "breast_screening",
    topic: "breast_cancer_screening",
    content:
      "Breast cancer screening via mammography is recommended for women aged 40-74 (USPSTF 2024 Grade B). Average risk: screening every 2 years. High risk (family history, BRCA): may start at age 30 with MRI. Baseline lifetime risk: ~12.9% for women (~1 in 8). Australian BreastScreen covers ages 40-74.",
    citations: ["USPSTF 2024 Grade B", "ACS 2025", "AU BreastScreen"],
    keywords: ["breast", "cancer", "mammogram", "screening", "mammography", "women", "brca"],
  },
  {
    id: "breast_risk_factors",
    topic: "breast_cancer_risk_factors",
    content:
      "Risk factors for breast cancer include: family history (first-degree relative: OR 2.46 for Asian women per Wang et al. 2023 PMID:37400822, ~2.5x generally), obesity (postmenopausal: HR 1.11 per 5 kg/m² per Fontvieille et al. 2023 PMID:37993940), alcohol (even light drinking RR 1.04 per Jun et al. 2023 PMID:37905315), and smoking (RR ~1.1). BRCA1/2 mutations carry 45-72% lifetime risk.",
    citations: [
      "Wang et al. 2023, BMC Med, PMID:37400822",
      "Fontvieille et al. 2023, BMC Med, PMID:37993940",
      "Jun et al. 2023, Epidemiol Health, PMID:37905315",
    ],
    keywords: ["breast", "cancer", "risk", "family", "obesity", "alcohol", "brca", "hormone"],
  },

  // ─── COLORECTAL / BOWEL CANCER ───
  {
    id: "colorectal_screening",
    topic: "colorectal_cancer_screening",
    content:
      "Colorectal cancer screening is recommended for adults aged 45-75 (USPSTF 2021 Grade A for 50-75, Grade B for 45-49). Methods: colonoscopy every 10 years, FIT annually, or stool DNA test every 1-3 years. High risk (family history): start at age 40 or 10 years before youngest affected relative. Baseline lifetime risk: ~4.3% males, ~4.0% females.",
    citations: ["USPSTF 2021 Grade A/B", "ACS 2025", "AU NBCSP", "SG Screen for Life"],
    keywords: ["colorectal", "colon", "bowel", "cancer", "colonoscopy", "screening", "fit", "stool"],
  },
  {
    id: "colorectal_risk_factors",
    topic: "colorectal_cancer_risk_factors",
    content:
      "Risk factors for colorectal cancer: family history (RR 1.87 per Mehraban Far 2019 PMID:31435179, 46 studies, n=906,981), obesity (independent risk per Hua et al. 2023, Fontvieille 2023), processed meat and high sodium diet (1.3x), alcohol (even light drinking significant per Jun 2023), smoking (1.2x), diabetes (1.3x), IBD, sedentary behavior (+12% per Hermelink 2022 PMID:35612669). Early-onset CRC risk factors include male sex, Caucasian, family history, IBD, obesity.",
    citations: [
      "Mehraban Far et al. 2019, World J Gastroenterol, PMID:31435179",
      "Hua et al. 2023, Front Oncol",
      "Hermelink et al. 2022, Eur J Epidemiol, PMID:35612669",
      "Jun et al. 2023, PMID:37905315",
    ],
    keywords: ["colorectal", "colon", "bowel", "cancer", "risk", "family", "obesity", "diet", "ibd", "early-onset"],
  },

  // ─── CERVICAL CANCER ───
  {
    id: "cervical_screening",
    topic: "cervical_cancer_screening",
    content:
      "Cervical cancer screening: Pap smear every 3 years (ages 21-29), Pap + HPV co-testing every 5 years or Pap alone every 3 years (ages 30-65). USPSTF 2018 Grade A. HPV vaccination is the primary prevention strategy. Baseline lifetime risk: ~0.6% for women. Australian NCSP covers ages 25-74.",
    citations: ["USPSTF 2018 Grade A", "AU NCSP", "ACS 2025"],
    keywords: ["cervical", "cancer", "pap", "smear", "hpv", "screening", "vaccination", "women"],
  },

  // ─── PROSTATE CANCER ───
  {
    id: "prostate_screening",
    topic: "prostate_cancer_screening",
    content:
      "Prostate cancer screening via PSA blood test is a shared decision between patient and doctor for men aged 55-69 (USPSTF 2018 Grade C). Not recommended for men 70+ (Grade D). African American men and those with family history may benefit from earlier discussion (age 40-45). Baseline lifetime risk: ~12.6% for men (~1 in 8). Family history: RR 2.5 (2.2-2.8) per Johns & Houlston 2003 PMID:12780833. African American men have ~70% higher incidence (DeSantis 2019 PMID:30762872).",
    citations: [
      "USPSTF 2018 Grade C/D",
      "Johns & Houlston 2003, BJU Int, PMID:12780833",
      "DeSantis et al. 2019, PMID:30762872",
    ],
    keywords: ["prostate", "cancer", "psa", "screening", "men", "male", "african", "family"],
  },

  // ─── LIVER CANCER ───
  {
    id: "liver_screening",
    topic: "liver_cancer_screening",
    content:
      "Liver cancer screening via ultrasound ± alpha-fetoprotein (AFP) every 6 months is recommended for high-risk individuals: those with hepatitis B/C, cirrhosis, or heavy alcohol use. No routine population screening. Baseline lifetime risk: ~1.1% males, ~0.5% females. Risk multipliers: hepatitis B/C 5.0x (conservative; Wang et al. 2025 PMID:39834076 umbrella review: HBV 12.5x, HCV 11.2x), cirrhosis 8.0x (Huang et al. 2023 PMID:35940513).",
    citations: [
      "ACS 2025",
      "Wang et al. 2025, Ann Med, PMID:39834076",
      "Huang et al. 2023, PMID:35940513",
    ],
    keywords: ["liver", "cancer", "hepatitis", "cirrhosis", "screening", "ultrasound", "afp", "hbv", "hcv"],
  },
  {
    id: "liver_risk_factors",
    topic: "liver_cancer_risk_factors",
    content:
      "Liver cancer risk factors: hepatitis B (12.5x) and C (11.2x) per Wang 2025, cirrhosis (8.0x, 5yr cumulative 3%, 10yr 9% per Huang 2023), heavy alcohol (2.5-5.0x per Jun 2023), obesity (1.8x for obese), diabetes (2.0x), smoking (1.5x). Smoking-alcohol interaction: multiplicative aOR 1.28 per Bui 2024 PMID:38533737. Asian and Hispanic populations have 1.8x higher incidence. ALDH2-deficient East Asians face significantly higher alcohol-related liver cancer risk (Ng et al. 2023 PMID:37268241).",
    citations: [
      "Wang et al. 2025, PMID:39834076",
      "Jun et al. 2023, PMID:37905315",
      "Bui et al. 2024, PMID:38533737",
      "Ng et al. 2023, PMID:37268241",
    ],
    keywords: ["liver", "cancer", "hepatitis", "alcohol", "obesity", "cirrhosis", "risk", "asian", "aldh2"],
  },

  // ─── SKIN CANCER / MELANOMA ───
  {
    id: "skin_screening",
    topic: "skin_cancer_screening",
    content:
      "Skin cancer (melanoma) screening: regular skin self-exams and annual dermatologist exam for high-risk individuals. USPSTF has insufficient evidence for general population screening. Risk factors: fair skin (Caucasians 3.0x), family history (RR 1.74 per Gandini et al. 2005 PMID:16125929, 60 studies), UV exposure, many moles, prior skin cancer. Baseline lifetime risk: ~3.3% males, ~2.3% females (melanoma).",
    citations: ["USPSTF", "Gandini et al. 2005, Eur J Cancer, PMID:16125929", "NCI SEER"],
    keywords: ["skin", "cancer", "melanoma", "screening", "mole", "uv", "sun", "dermatologist"],
  },

  // ─── GASTRIC / STOMACH CANCER ───
  {
    id: "gastric_screening",
    topic: "gastric_cancer_screening",
    content:
      "Gastric (stomach) cancer: no routine population screening in Western countries. High-risk groups (Asian, African American, Hispanic at 1.7-2.0x risk) may benefit from endoscopy screening. Family history: RR 2.35 (Yaghoobi 2017 PMID:28428723, 32 studies). Risk factors: H. pylori infection, high-salt diet (1.5x), smoking (1.6x), alcohol. Metabolic syndrome increases GI cancer risk (Zhan 2024 PMID:38230882).",
    citations: [
      "Yaghoobi et al. 2017, World J Gastroenterol, PMID:28428723",
      "Zhan et al. 2024, J Gastroenterol Hepatol, PMID:38230882",
      "Lui et al. 2014, PMID:25030941",
    ],
    keywords: ["gastric", "stomach", "cancer", "screening", "h pylori", "diet", "salt", "asian", "endoscopy"],
  },

  // ─── OVARIAN CANCER ───
  {
    id: "ovarian_screening",
    topic: "ovarian_cancer_screening",
    content:
      "Ovarian cancer: no effective routine screening for average-risk women. USPSTF recommends against routine screening (Grade D). High-risk women (BRCA1/2, strong family history) should discuss with their doctor. Family history: RR 3.1 (2.6-3.7) per Stratton et al. 1998 PMID:9637117. Other risk factors: endometriosis (1.5x), PCOS (1.3x), obesity. Baseline lifetime risk: ~1.2%.",
    citations: ["USPSTF Grade D", "Stratton et al. 1998, PMID:9637117"],
    keywords: ["ovarian", "cancer", "screening", "brca", "family", "women", "endometriosis"],
  },

  // ─── UTERINE / ENDOMETRIAL CANCER ───
  {
    id: "uterine_screening",
    topic: "uterine_cancer_screening",
    content:
      "Uterine (endometrial) cancer: no routine population screening. Report any abnormal vaginal bleeding to your doctor. Obesity is the strongest risk factor: overweight 1.5x, obese 2.5x, severely obese 4.5x (Chen 2023 PMID:37139871). Family history: RR ~2.0 (Win et al. 2015 PMID:25560109). Endometriosis increases risk 1.8x. Baseline lifetime risk: ~3.1%.",
    citations: [
      "ACS 2025",
      "Chen et al. 2023, PMID:37139871",
      "Win et al. 2015, PMID:25560109",
    ],
    keywords: ["uterine", "endometrial", "cancer", "screening", "obesity", "bleeding", "women"],
  },

  // ─── THYROID CANCER ───
  {
    id: "thyroid_screening",
    topic: "thyroid_cancer_screening",
    content:
      "Thyroid cancer: USPSTF recommends against routine screening in asymptomatic adults (Grade D). Report neck lumps or swallowing difficulty to your doctor. Family history: SIR 2.9 (2.4-3.4) per Fallah et al. 2013 PMID:23585692; ≥2 relatives → 10% cumulative risk. Radiation exposure increases risk 3.0x. Obesity: 1.2-1.3x. Baseline lifetime risk: ~0.7% males, ~2.1% females.",
    citations: ["USPSTF Grade D", "Fallah et al. 2013, J Med Genet, PMID:23585692"],
    keywords: ["thyroid", "cancer", "screening", "neck", "lump", "radiation", "family"],
  },

  // ─── TESTICULAR CANCER ───
  {
    id: "testicular_screening",
    topic: "testicular_cancer_screening",
    content:
      "Testicular cancer: USPSTF recommends against routine screening (Grade D). Monthly self-exams are encouraged for young men (ages 15-35). Family history is a very strong risk factor: SIR 4x (father), 8-9x (brother) per Hemminki & Li 2004 PMID:15208620. Baseline lifetime risk: ~0.4%. Most common cancer in men aged 15-35.",
    citations: ["USPSTF Grade D", "Hemminki & Li 2004, Br J Cancer, PMID:15208620"],
    keywords: ["testicular", "cancer", "screening", "self-exam", "young", "men", "male"],
  },

  // ─── ALCOHOL AND CANCER ───
  {
    id: "alcohol_cancer_risk",
    topic: "alcohol_and_cancer",
    content:
      "There is no safe level of alcohol consumption for cancer risk. Jun et al. 2023 (PMID:37905315) meta-analysis of 139 cohort studies: even light drinking (0.01-12.4g/day) significantly increases esophageal, colorectal, and breast cancer risk. Heavy drinking (≥50g/day) elevates risk for nearly all cancer types. Specific risks: liver cancer 2.5-5.0x for heavy drinkers, breast 1.04-1.6x, colorectal 1.05-1.5x. ALDH2-deficient East Asians face even higher risk (Ng et al. 2023 PMID:37268241).",
    citations: [
      "Jun et al. 2023, Epidemiol Health, PMID:37905315",
      "Ng et al. 2023, Ann Epidemiol, PMID:37268241",
      "WCRF/AICR Third Expert Report 2018",
    ],
    keywords: ["alcohol", "drinking", "cancer", "risk", "liver", "breast", "safe", "level"],
  },

  // ─── OBESITY AND CANCER ───
  {
    id: "obesity_cancer_risk",
    topic: "obesity_and_cancer",
    content:
      "Obesity is linked to 13+ cancer types. Chen et al. 2023 (PMID:37139871) umbrella review: each 5 kg/m² BMI increase → 1.01-1.13x risk for multiple cancers. Strongest associations: endometrial (4.5x for severe obesity), kidney (6% per 1 kg/m²), liver (1.8x), colorectal (1.3-1.5x). Fontvieille et al. 2023 (PMID:37993940) found HR 1.11 per ~5 kg/m² for obesity-related cancers in 577,343 adults, with additive interaction between obesity and cardiovascular disease (RERI 0.28).",
    citations: [
      "Chen et al. 2023, Nutr Cancer, PMID:37139871",
      "Fontvieille et al. 2023, BMC Med, PMID:37993940",
      "WCRF/AICR 2018",
    ],
    keywords: ["obesity", "bmi", "weight", "overweight", "cancer", "risk", "body mass", "fat"],
  },

  // ─── SEDENTARY BEHAVIOR ───
  {
    id: "sedentary_cancer_risk",
    topic: "sedentary_behavior_and_cancer",
    content:
      "Sedentary behavior increases cancer risk. Hermelink et al. 2022 (PMID:35612669) umbrella review: high sedentary behavior associated with +36% endometrial cancer risk, +12% colon cancer risk, and increased lung cancer risk. Regular physical activity (150+ min/week moderate exercise) can reduce cancer risk.",
    citations: ["Hermelink et al. 2022, Eur J Epidemiol, PMID:35612669"],
    keywords: ["sedentary", "sitting", "exercise", "physical", "activity", "cancer", "risk", "colon", "endometrial"],
  },

  // ─── FAMILY HISTORY GENERAL ───
  {
    id: "family_history_general",
    topic: "family_history_and_cancer",
    content:
      "Family history of cancer in a first-degree relative significantly increases risk for most cancer types. Key risk ratios: ovarian 3.1x (Stratton 1998), testicular 4-9x (Hemminki 2004), thyroid 2.9x (Fallah 2013), prostate 2.5x (Johns 2003), breast 2.5x (Wang 2023), gastric 2.35x (Yaghoobi 2017), uterine 2.0x (Win 2015), lung 1.5-2.0x (Ang 2020), colorectal 1.87x (Mehraban Far 2019), skin/melanoma 1.74x (Gandini 2005). Having ≥2 affected relatives further increases risk. Discuss family history with your doctor for personalized screening.",
    citations: [
      "Multiple meta-analyses — see individual cancer sections for PMIDs",
    ],
    keywords: ["family", "history", "hereditary", "genetic", "relative", "parent", "sibling", "risk", "cancer"],
  },

  // ─── DIABETES SCREENING ───
  {
    id: "diabetes_screening",
    topic: "diabetes_screening",
    content:
      "Diabetes screening: USPSTF Grade B recommends screening adults aged 35-70 who are overweight or obese using fasting plasma glucose (FPG) or HbA1c. ADA 2024 recommends screening at age 45+ or earlier with risk factors. Higher-risk ethnicities (Asian, African American, Hispanic) should screen at BMI ≥23. Screening interval: every 3 years if normal, annually if risk factors present. Pre-diabetic individuals should retest every 6-12 months.",
    citations: ["USPSTF 2021 Grade B", "ADA Standards of Care 2024", "CDC 2024"],
    keywords: ["diabetes", "blood sugar", "glucose", "hba1c", "screening", "prediabetes", "insulin", "type 2"],
  },
  {
    id: "diabetes_management",
    topic: "diabetes_management",
    content:
      "Diabetes management: maintain HbA1c below 7% (or doctor's target). Annual eye exam, foot exam, kidney function test. 150+ min/week moderate exercise. Pre-diabetes prevention: 5-7% body weight loss, increase fiber, limit refined carbs. Metformin may be considered for diabetes prevention. Diabetes increases cancer risk: colorectal 1.3x, liver 2.0x, breast 1.2x.",
    citations: ["ADA 2024", "CDC 2024"],
    keywords: ["diabetes", "management", "hba1c", "prevention", "metformin", "exercise", "diet", "cancer"],
  },

  // ─── CHOLESTEROL SCREENING ───
  {
    id: "cholesterol_screening",
    topic: "cholesterol_screening",
    content:
      "Cholesterol screening via lipid panel (total cholesterol, LDL, HDL, triglycerides) is recommended for adults 20+ (baseline) and regularly for ages 40-75. ACC/AHA 2019 guideline; USPSTF Grade B for adults 40-75. Interval: every 4-6 years if normal, annually if moderate risk, every 6-12 months if high. Risk factors: age 40+, smoking, diabetes, hypertension, obesity, male 45+, female 55+.",
    citations: ["ACC/AHA 2019", "USPSTF 2023 Grade B"],
    keywords: ["cholesterol", "lipid", "ldl", "hdl", "triglycerides", "screening", "heart", "statin", "cardiovascular"],
  },

  // ─── BLOOD PRESSURE SCREENING ───
  {
    id: "blood_pressure_screening",
    topic: "blood_pressure_screening",
    content:
      "Blood pressure screening is recommended for all adults 18+ (USPSTF Grade A). AHA/ACC 2023 defines hypertension as ≥130/80 mmHg. Interval: annually for normal, every 3-6 months if elevated, every 1-3 months if high until controlled. Risk factors: age 45+, African American ethnicity (higher prevalence), obesity, smoking, diabetes, high cholesterol, moderate-to-heavy alcohol. DASH diet and sodium reduction (<2,300 mg/day, ideally <1,500 mg) are key interventions.",
    citations: ["AHA/ACC 2023", "USPSTF 2021 Grade A", "WHO 2023"],
    keywords: ["blood pressure", "hypertension", "screening", "dash", "sodium", "heart", "cardiovascular", "stroke"],
  },

  // ─── GENERAL PREVENTION ───
  {
    id: "cancer_prevention_general",
    topic: "cancer_prevention",
    content:
      "Evidence-based cancer prevention strategies: 1) Don't smoke or quit smoking (reduces lung, colorectal, liver, gastric, cervical cancer risk). 2) Limit alcohol (no safe level for cancer risk). 3) Maintain healthy weight (obesity linked to 13+ cancers). 4) Exercise regularly (150+ min/week moderate activity). 5) Eat a healthy diet (limit processed meat, increase fruits/vegetables/fiber). 6) Protect skin from UV (sunscreen, avoid tanning beds). 7) Get vaccinated (HPV for cervical cancer, Hepatitis B for liver cancer). 8) Get screened on schedule. 9) Know your family history.",
    citations: ["WCRF/AICR 2018", "ACS 2025", "USPSTF 2024"],
    keywords: ["prevention", "cancer", "lifestyle", "diet", "exercise", "smoking", "alcohol", "vaccine", "sunscreen"],
  },

  // ─── SMOKING-ALCOHOL INTERACTION ───
  {
    id: "smoking_alcohol_interaction",
    topic: "smoking_alcohol_interaction",
    content:
      "Combined smoking and alcohol use creates synergistic cancer risk exceeding additive effects. Bui et al. 2024 (PMID:38533737): multiplicative aOR 1.28 for liver cancer in Korean men with metabolic syndrome; additive SI 2.75. Burton et al. 2024 (PMID:38000113): synergy index for head/neck cancer 3.78, alcohol×weight→liver 1.55. For gastric cancer, Holmes 2021 found sub-additive interaction (SI 0.88) — risk driven mainly by smoking alone.",
    citations: [
      "Bui et al. 2024, Int J Cancer, PMID:38533737",
      "Burton et al. 2024, Public Health, PMID:38000113",
    ],
    keywords: ["smoking", "alcohol", "interaction", "synergy", "combined", "liver", "cancer", "risk"],
  },
];

export default healthKnowledgeBase;
